import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Crown, ShieldAlert, ChevronDown, Menu } from 'lucide-react';
import AuthButton from '@/components/AuthButton';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import SkinSwitcher from '@/components/SkinSwitcher';
import ThemeToggle from '@/components/ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminEmail } from '@/lib/adminAccess';
import { pickByLang } from '@/i18n';

export default function HomeHeader() {
  const { lang, t, localePath } = useI18n();
  const { isPremium, user } = useAuth();
  const isAdmin = isAdminEmail(user?.email);
  const [mobileOpen, setMobileOpen] = useState(false);
  const practiceLabel = pickByLang({
    ko: '연습', ja: '練習', 'zh-CN': '练习', 'zh-TW': '練習',
    vi: 'Luyện tập', th: 'ฝึก', id: 'Latihan', ms: 'Latihan',
    my: 'လေ့ကျင့်ခန်း', es: 'Práctica', 'pt-BR': 'Prática',
    fr: 'Pratique', de: 'Üben', ar: 'تدريب', en: 'Practice',
  }, lang, 'en');
  const resourcesLabel = pickByLang({
    ko: '학습 자료', ja: '学習資料', 'zh-CN': '学习资料', 'zh-TW': '學習資料',
    vi: 'Tài liệu', th: 'แหล่งเรียนรู้', id: 'Materi', ms: 'Bahan',
    my: 'သင်ခန်းစာများ', es: 'Recursos', 'pt-BR': 'Recursos',
    fr: 'Ressources', de: 'Material', ar: 'موارد', en: 'Resources',
  }, lang, 'en');
  const closeMobile = () => setMobileOpen(false);

  // Languages with long translations need smaller text/gaps to fit on one line
  const isCJK = ['ja', 'ko', 'zh-CN', 'zh-TW'].includes(lang);
  const isLong = ['my', 'th', 'vi', 'id', 'ms', 'de', 'es', 'fr', 'pt-br', 'ar'].includes(lang);
  const navSize = lang === 'my'
    ? 'gap-1.5 text-[11px]'
    : (isLong || isCJK)
      ? 'gap-2 text-xs'
      : 'gap-3 text-[13px]';
  const linkBase = 'shrink-0 whitespace-nowrap hover:text-foreground transition-colors';

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 flex items-center justify-between gap-2 sm:gap-3">
        <Link to={localePath('/')} className="flex items-center gap-2 min-w-0 shrink">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
          </div>
          <span className="text-base sm:text-xl font-bold font-display text-foreground tracking-tight whitespace-nowrap truncate">
            POWER <span className="text-accent">TOEFL</span>
          </span>
        </Link>

        <nav className={`hidden lg:flex flex-1 items-center justify-center flex-nowrap min-w-0 overflow-hidden text-muted-foreground ${navSize}`}>
          <Link to={localePath('/questions')} className={`${linkBase} font-medium`}>{t.navQuestionPool}</Link>
          {/* Practice dropdown groups Speaking + Writing + CAT to reduce header items 8 → 5 */}
          <DropdownMenu>
            <DropdownMenuTrigger className={`${linkBase} inline-flex items-center gap-0.5 outline-none focus-visible:text-foreground`}>
              {pickByLang({
                ko: '연습', ja: '練習', 'zh-CN': '练习', 'zh-TW': '練習',
                vi: 'Luyện tập', th: 'ฝึก', id: 'Latihan', ms: 'Latihan',
                my: 'လေ့ကျင့်ခန်း', es: 'Práctica', 'pt-BR': 'Prática',
                fr: 'Pratique', de: 'Üben', ar: 'تدريب', en: 'Practice',
              }, lang, 'en')}
              <ChevronDown className="w-3 h-3" aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="bg-popover border-border z-50">
              <DropdownMenuItem asChild>
                <Link to={localePath('/questions/speaking')}>{t.navSpeaking}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={localePath('/questions/writing')}>{t.navWriting}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={localePath('/questions/cat')}>{t.catLabel}</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Resources dropdown groups Score chart + Vocabulary + Guide to keep the bar scannable */}
          <DropdownMenu>
            <DropdownMenuTrigger className={`${linkBase} inline-flex items-center gap-0.5 outline-none focus-visible:text-foreground`}>
              {resourcesLabel}
              <ChevronDown className="w-3 h-3" aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="bg-popover border-border z-50">
              <DropdownMenuItem asChild>
                <Link to={localePath('/score-chart')}>{t.navScoreChart}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={localePath('/vocabulary')}>{t.navVocabulary}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={localePath('/guide')}>{t.guideLabel}</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to={localePath('/pricing')} className={`${linkBase} text-accent font-semibold`}>{t.navPremium}</Link>

        </nav>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <Link
              to={localePath('/admin')}
              className="hidden sm:inline-flex h-8 w-8 2xl:w-auto items-center justify-center gap-1.5 rounded-full bg-primary/10 px-0 2xl:px-3 text-primary text-xs font-bold hover:bg-primary/20 transition-colors whitespace-nowrap shrink-0 leading-none"
              title="마스터 어드민 보드"
              aria-label="마스터 어드민 보드"
            >
              <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden 2xl:inline">Admin</span>
            </Link>
          )}
          {isPremium && (
            <div className="hidden sm:inline-flex h-8 w-8 2xl:w-auto items-center justify-center gap-1.5 rounded-full bg-secondary/15 px-0 2xl:px-3 text-secondary text-xs font-bold whitespace-nowrap shrink-0 leading-none">
              <Crown className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden 2xl:inline">{t.proBadgeLabel}</span>
            </div>
          )}
          <LanguageSwitcher />
          {/* Skin/theme switchers are secondary — hidden on small screens so the
              sign-in button and menu never overflow the 360–390px viewport. */}
          <span className="hidden md:inline-flex items-center gap-2">
            <SkinSwitcher />
            <ThemeToggle />
          </span>
          <AuthButton />

          {/* Mobile hamburger menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-muted transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-card">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 mt-6 text-sm">
                <Link onClick={closeMobile} to={localePath('/questions')} className="px-3 py-2.5 rounded-md hover:bg-muted font-medium">{t.navQuestionPool}</Link>
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-2">{practiceLabel}</div>
                <Link onClick={closeMobile} to={localePath('/questions/speaking')} className="px-3 py-2.5 rounded-md hover:bg-muted">{t.navSpeaking}</Link>
                <Link onClick={closeMobile} to={localePath('/questions/writing')} className="px-3 py-2.5 rounded-md hover:bg-muted">{t.navWriting}</Link>
                <Link onClick={closeMobile} to={localePath('/questions/cat')} className="px-3 py-2.5 rounded-md hover:bg-muted">{t.catLabel}</Link>
                <div className="h-px bg-border my-2" />
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{resourcesLabel}</div>
                <Link onClick={closeMobile} to={localePath('/score-chart')} className="px-3 py-2.5 rounded-md hover:bg-muted">{t.navScoreChart}</Link>
                <Link onClick={closeMobile} to={localePath('/vocabulary')} className="px-3 py-2.5 rounded-md hover:bg-muted">{t.navVocabulary}</Link>
                <Link onClick={closeMobile} to={localePath('/guide')} className="px-3 py-2.5 rounded-md hover:bg-muted">{t.guideLabel}</Link>
                <div className="h-px bg-border my-2" />

                <Link onClick={closeMobile} to={localePath('/pricing')} className="px-3 py-2.5 rounded-md hover:bg-muted text-accent font-semibold">{t.navPremium}</Link>
                {isAdmin && (
                  <>
                    <div className="h-px bg-border my-2" />
                    <Link onClick={closeMobile} to={localePath('/admin')} className="px-3 py-2.5 rounded-md hover:bg-muted inline-flex items-center gap-2 text-primary font-semibold">
                      <ShieldAlert className="w-4 h-4" /> Admin
                    </Link>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
